import { createAsyncThunk } from '@reduxjs/toolkit';
import { errMsg } from '../utils/errMsg';
import {
    setFollowedArtists,
    addFollow,
    removeFollow,
    updateFollowedSet,
    updateNotifySet,
    setLoading,
    setError,
} from '../slices/followedArtistsSlice';

import { refreshAndRetry } from '../utils/refreshAndRetry';
import { asCatalogId } from './likesThunk';
import { entityId } from '../../utils/entityId';
import { track } from '../../utils/analytics';
import { emitNotifyToast } from '../../utils/notifyToast';

export const fetchFollowedArtists = createAsyncThunk(
    'followedArtists/fetch',
    async ({ page = 1, limit = 50 } = {}, { dispatch }) => {
        try {
            dispatch(setLoading(true));

            const result = await refreshAndRetry(
                (token) => window.electron.library.getFollowedArtists(token, page, limit)
            );

            if (result.error) {
                dispatch(setError(errMsg(result.error)));
                return;
            }

            const data = result.data || [];
            const total = result.pagination?.total || 0;

            dispatch(setFollowedArtists({ data, total }));

            const setUpdate = {};
            const notifyUpdate = {};
            for (const artist of data) {
                setUpdate[artist.artist_name] = true;
                notifyUpdate[artist.artist_name] = !!artist.notify;
            }
            dispatch(updateFollowedSet(setUpdate));
            dispatch(updateNotifySet(notifyUpdate));
        } catch (error) {
            console.error('Error fetching followed artists:', error);
            dispatch(setError(errMsg(error)));
        } finally {
            dispatch(setLoading(false));
        }
    }
);

// Follow-outcome notifications for ConfirmationToastBridge — the same
// window-event hop as likesThunk's LIKE_TOAST_EVENT (thunks run outside React).
export const FOLLOW_TOAST_EVENT = 'mg:follow-toast';
function emitFollowToast(detail) {
    if (typeof window === 'undefined') return;
    try { window.dispatchEvent(new CustomEvent(FOLLOW_TOAST_EVENT, { detail })); } catch { /* non-DOM env */ }
}

// Resolves true when the server saved the follow, false when it was reverted.
// `artist.quiet` skips the confirmation card (bulk flows would stack one per
// artist); failures always surface.
export const followArtist = createAsyncThunk(
    'followedArtists/follow',
    async (artist, { dispatch }) => {
        try {
            const artwork = artist.artwork || artist.artworkUrl || null;

            // Optimistic update
            dispatch(addFollow({
                artist_name: artist.name,
                artwork,
                created_at: new Date().toISOString(),
            }));

            // "<Artist> was added to your Favourite Artists." — fired with the
            // optimistic flip so it lands with the button change; a failed
            // save below reverts the follow and says so.
            emitFollowToast({ status: 'followed', artistName: artist.name, artwork, from: artist.from || null, quiet: !!artist.quiet });

            // Same guard as track likes (likesThunk asCatalogId): never persist
            // a foreign integer as the catalog id (2026-08-06 poisoned-likes
            // incident class — internal artist_ids are ints too).
            const artistCatalogId = asCatalogId(entityId(artist));
            const result = await refreshAndRetry(
                (token) => window.electron.library.followArtist(token, {
                    // music-info-server id of the artist (id-first; name stays as fallback)
                    id: artistCatalogId,
                    artistName: artist.name,
                    artwork,
                })
            );

            if (result.error) {
                dispatch(removeFollow({ artistName: artist.name }));
                emitFollowToast({ status: 'failed', artistName: artist.name });
                console.error('Follow failed:', result.error);
                return false;
            }
            track('artist_followed', {
                artist_id: artistCatalogId || undefined,
                artist_name: artist.name,
                from: artist.from || 'button',
            });
            return true;
        } catch (error) {
            dispatch(removeFollow({ artistName: artist.name }));
            emitFollowToast({ status: 'failed', artistName: artist.name });
            console.error('Error following artist:', error);
            return false;
        }
    }
);

export const unfollowArtist = createAsyncThunk(
    'followedArtists/unfollow',
    async ({ artistName, from, artwork, quiet }, { dispatch, getState }) => {
        try {
            // Snapshot BEFORE the optimistic remove: the "was removed from your
            // Favourite Artists — Undo" card re-follows from it, and the bell
            // must come back too (notify lives on the follow row, so
            // unfollowing drops it server-side).
            const fa = getState().followedArtists || {};
            const row = (fa.artists || []).find((a) => a.artist_name === artistName) || null;
            const wasNotify = !!(fa.notifySet || {})[artistName];
            const art = row?.artwork || artwork || null;

            // Optimistic update
            dispatch(removeFollow({ artistName }));

            emitFollowToast({
                status: 'unfollowed',
                artistName,
                artwork: art,
                from: from || null,
                quiet: !!quiet,
                wasNotify,
                // What followArtist needs to restore the follow. Catalog id from
                // the explicit public-id column only — row.id is the follow
                // row's own serial.
                undoArtist: { name: artistName, artwork: art, id: asCatalogId(row?.artist_public_id) || undefined },
            });

            const result = await refreshAndRetry(
                (token) => window.electron.library.unfollowArtist(token, artistName)
            );

            if (result.error) {
                console.error('Unfollow failed:', result.error);
            } else {
                track('artist_unfollowed', { artist_name: artistName, from: from || undefined });
            }
        } catch (error) {
            console.error('Error unfollowing artist:', error);
        }
    }
);

export const checkFollowedArtists = createAsyncThunk(
    'followedArtists/check',
    async (artistNames, { dispatch }) => {
        try {
            const result = await refreshAndRetry(
                (token) => window.electron.library.checkFollowedArtists(token, artistNames)
            );

            if (result.error) {
                console.error('Check followed failed:', result.error);
                return;
            }

            const data = result.data || [];
            const setUpdate = {};
            const notifyUpdate = {};
            artistNames.forEach((name, i) => {
                // New servers answer { followed, notify } (we send withNotify);
                // an old server during the rollout window still answers bare
                // booleans — handle both.
                const entry = data[i];
                if (entry && typeof entry === 'object') {
                    setUpdate[name] = !!entry.followed;
                    notifyUpdate[name] = !!entry.notify;
                } else {
                    setUpdate[name] = !!entry;
                }
            });
            dispatch(updateFollowedSet(setUpdate));
            dispatch(updateNotifySet(notifyUpdate));
        } catch (error) {
            console.error('Error checking followed artists:', error);
        }
    }
);

export const setArtistNotify = createAsyncThunk(
    'followedArtists/setNotify',
    async ({ artistName, notify, from, artwork, quiet }, { dispatch, getState }) => {
        // Confirmation card for ConfirmationToastBridge; artwork falls back to
        // the loaded follow row. `quiet` skips the card (the unfollow Undo's
        // silent bell restore) — failures always surface.
        const row = (getState().followedArtists?.artists || []).find((a) => a.artist_name === artistName);
        const toast = { entityType: 'artist', name: artistName, artwork: artwork || row?.artwork || null, notify, quiet: !!quiet };
        try {
            // Optimistic toggle, reverted on failure (same pattern as follow).
            dispatch(updateNotifySet({ [artistName]: notify }));
            emitNotifyToast({ status: 'ok', ...toast });

            const result = await refreshAndRetry(
                (token) => window.electron.library.setArtistNotify(token, artistName, notify)
            );

            if (result.error) {
                dispatch(updateNotifySet({ [artistName]: !notify }));
                emitNotifyToast({ status: 'failed', ...toast });
                console.error('Set artist notify failed:', result.error);
            } else {
                track('artist_notify_toggled', {
                    artist_name: artistName,
                    notify,
                    from: from || 'button',
                });
            }
        } catch (error) {
            dispatch(updateNotifySet({ [artistName]: !notify }));
            console.error('Error setting artist notify:', error);
        }
    }
);
